/**
 * B-23: pure helpers for the 鐵粉月卡 CMS panels and the monthly report page.
 * No imports from services or React so `npx --prefix functions tsx --test cms/tests/fan-pass/*.test.ts` can run them.
 */

export const FAN_PASS_PRICE_MIN = 100;
export const FAN_PASS_PRICE_MAX = 5000;
export const FAN_PASS_BENEFITS_MAX = 6;
export const FAN_PASS_BENEFIT_MAX_CHARS = 40;

/** Counts user-perceived characters (CJK and emoji count as 1), matching the backend's `[...str].length`. */
export const charCount = (value: string) => [...value].length;

export interface FanPassConfigDraft {
  enabled: boolean;
  priceCoins: number;
  benefits: string[];
  promoEnabled: boolean;
  promoCoins: number;
  /** "YYYY-MM-DD" (local date; promo valid through end of that day). */
  promoUntil: string;
}

export function validateFanPassConfig(draft: FanPassConfigDraft, now = Date.now()): string[] {
  const errors: string[] = [];
  if (!Number.isInteger(draft.priceCoins) || draft.priceCoins < FAN_PASS_PRICE_MIN || draft.priceCoins > FAN_PASS_PRICE_MAX) {
    errors.push(`價格需為 ${FAN_PASS_PRICE_MIN}–${FAN_PASS_PRICE_MAX} 的整數金幣`);
  }
  const benefits = cleanBenefits(draft.benefits);
  if (benefits.length > FAN_PASS_BENEFITS_MAX) errors.push(`權益最多 ${FAN_PASS_BENEFITS_MAX} 條`);
  benefits.forEach((b, i) => {
    if (charCount(b) > FAN_PASS_BENEFIT_MAX_CHARS) errors.push(`第 ${i + 1} 條權益超過 ${FAN_PASS_BENEFIT_MAX_CHARS} 字`);
  });
  if (draft.enabled && benefits.length === 0) errors.push("開賣時至少需要 1 條權益文案");
  if (draft.promoEnabled) {
    if (!Number.isInteger(draft.promoCoins) || draft.promoCoins < 1 || draft.promoCoins >= draft.priceCoins) {
      errors.push("首期優惠金幣需為 1 以上、且低於原價的整數");
    }
    const until = endOfDayMs(draft.promoUntil);
    if (until === null) errors.push("請選擇優惠截止日");
    else if (until <= now) errors.push("優惠截止日必須晚於現在");
  }
  return errors;
}

/** Trims and drops empty lines; order is kept. */
export const cleanBenefits = (benefits: string[]) => benefits.map((b) => b.trim()).filter(Boolean);

const parseDate = (value: string): [number, number, number] | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return [y, m, d];
};
/** `<input type="date">` value → local end of that day (epoch ms). */
export function endOfDayMs(value: string): number | null {
  const p = parseDate(value);
  return p ? new Date(p[0], p[1] - 1, p[2], 23, 59, 59, 999).getTime() : null;
}
/** `<input type="date">` value → local start of that day (epoch ms). */
export function startOfDayMs(value: string): number | null {
  const p = parseDate(value);
  return p ? new Date(p[0], p[1] - 1, p[2]).getTime() : null;
}
/** epoch ms (or an already formatted "YYYY-MM-DD") → `<input type="date">` value in local time. */
export function toDateInput(value: number | string | null | undefined): string {
  if (typeof value === "string") return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ---------- 收款資料 ---------- */

/** 0.7 → "70"; keeps up to 2 decimals (e.g. 0.655 → "65.5"). */
export const rateToPercent = (rate: number) => String(Math.round(rate * 10000) / 100);
/** "70" / "70%" / "65.5" → 0.7 / 0.655; null when not a number in 0–100. */
export function percentToRate(value: string): number | null {
  const n = Number(value.trim().replace(/%$/, ""));
  if (!value.trim() || !Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100) / 10000;
}

export interface PayeeDraft {
  payeeType: "person" | "company";
  name: string;
  taxId: string;
  sharePercent: string;
  effectiveFrom: string;
}
export function validatePayee(draft: PayeeDraft): string[] {
  const errors: string[] = [];
  const name = draft.name.trim();
  if (!name) errors.push("請填寫收款人名稱");
  else if (charCount(name) > 100) errors.push("收款人名稱 ≤100 字");
  const taxId = draft.taxId.trim();
  // 統一編號 8 碼；個人身分證字號 1 英文 + 9 數字。只檢查格式，不驗檢查碼（由財務確認）。
  if (taxId && draft.payeeType === "company" && !/^\d{8}$/.test(taxId)) errors.push("公司統一編號需為 8 位數字");
  if (taxId && draft.payeeType === "person" && !/^[A-Z][12A-D89]\d{8}$/i.test(taxId)) errors.push("身分證／居留證號格式不正確");
  if (percentToRate(draft.sharePercent) === null) errors.push("創作者分潤比例需為 0–100%");
  if (startOfDayMs(draft.effectiveFrom) === null) errors.push("請選擇生效日");
  return errors;
}

/* ---------- 月報 ---------- */

export interface ReportRowLike {
  artistId: string;
  displayName: string;
  joins: number;
  renews: number;
  paidCoins: number;
  unitPriceNtd: number;
  grossNtd: number;
  platformFeeNtd: number;
  creatorShareNtd: number;
  platformShareNtd: number;
  estimated?: boolean;
}
export interface ReportTotals {
  joins: number;
  renews: number;
  paidCoins: number;
  grossNtd: number;
  platformFeeNtd: number;
  creatorShareNtd: number;
  platformShareNtd: number;
}
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Sums the row columns (rounded to 0.01 NTD). The page prefers the backend `totals` and falls back to this. */
export function sumReportRows(rows: ReportRowLike[]): ReportTotals {
  const t = rows.reduce(
    (acc, r) => ({
      joins: acc.joins + (r.joins || 0),
      renews: acc.renews + (r.renews || 0),
      paidCoins: acc.paidCoins + (r.paidCoins || 0),
      grossNtd: acc.grossNtd + (r.grossNtd || 0),
      platformFeeNtd: acc.platformFeeNtd + (r.platformFeeNtd || 0),
      creatorShareNtd: acc.creatorShareNtd + (r.creatorShareNtd || 0),
      platformShareNtd: acc.platformShareNtd + (r.platformShareNtd || 0),
    }),
    { joins: 0, renews: 0, paidCoins: 0, grossNtd: 0, platformFeeNtd: 0, creatorShareNtd: 0, platformShareNtd: 0 },
  );
  return {
    ...t,
    grossNtd: round2(t.grossNtd),
    platformFeeNtd: round2(t.platformFeeNtd),
    creatorShareNtd: round2(t.creatorShareNtd),
    platformShareNtd: round2(t.platformShareNtd),
  };
}

/** Merges backend totals over the computed ones so a partial `totals` object never shows blanks. */
export function resolveTotals(rows: ReportRowLike[], backend: Partial<ReportTotals> | null | undefined): ReportTotals {
  const computed = sumReportRows(rows);
  if (!backend) return computed;
  const out = { ...computed };
  for (const key of Object.keys(computed) as (keyof ReportTotals)[]) {
    const v = backend[key];
    if (typeof v === "number" && Number.isFinite(v)) out[key] = v;
  }
  return out;
}

const csvCell = (value: string | number | boolean) => {
  const s = String(value);
  // Prefix formula-looking text so spreadsheet apps never evaluate it (CSV injection).
  const safe = /^[=+\-@\t\r]/.test(s) && typeof value === "string" ? `'${s}` : s;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};
export const REPORT_CSV_HEADER = [
  "月份",
  "藝人 ID",
  "藝人",
  "新開通",
  "續期",
  "扣除購買金幣",
  "單價(NTD/枚)",
  "毛收入(NTD)",
  "平台費 30%(NTD)",
  "創作者分潤(NTD)",
  "平台分潤(NTD)",
  "單價為預估",
];

const UTF8_BOM = String.fromCharCode(0xfeff);
/** CSV with a UTF-8 BOM so Excel opens the Chinese headers correctly. */
export function reportToCsv(month: string, rows: ReportRowLike[], totals: ReportTotals): string {
  const lines = [REPORT_CSV_HEADER.map(csvCell).join(",")];
  for (const r of rows) {
    lines.push(
      [
        month,
        r.artistId,
        r.displayName,
        r.joins,
        r.renews,
        r.paidCoins,
        r.unitPriceNtd,
        r.grossNtd,
        r.platformFeeNtd,
        r.creatorShareNtd,
        r.platformShareNtd,
        r.estimated ? "是" : "否",
      ]
        .map(csvCell)
        .join(","),
    );
  }
  lines.push(
    [month, "", "合計", totals.joins, totals.renews, totals.paidCoins, "", totals.grossNtd, totals.platformFeeNtd, totals.creatorShareNtd, totals.platformShareNtd, ""]
      .map(csvCell)
      .join(","),
  );
  return `${UTF8_BOM}${lines.join("\r\n")}\r\n`;
}

/** Current and the previous `count - 1` months as "YYYY-MM", newest first (local calendar). */
export function recentMonths(count: number, now = new Date()): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}
export const isMonth = (value: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
